import { matchPath } from 'react-router';

import { all, call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { REGISTRATION_MIN_SIZE } from 'components/interventions/modals/ImportTScanModal';
import { restClient } from 'config/store/configureStore';

import { PROJECTION } from 'constants/ApiConstants';
import { SENT } from 'constants/InterventionConstants';
import { PARTS, TECHNICIAN } from 'constants/ModeConstants';
import { INTERVENTION_PATHS_BY_TYPE, PATH } from 'constants/RoutesConstants';

import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';
import { getBodyScans } from 'modules/bodyScan/actions';
import { OPEN_BODY_SCANS_IMPORT_MODAL } from 'modules/bodyScan/actionTypes';
import { selectDealer } from 'modules/dealers/actions';
import * as interventionCoreActions from 'modules/interventions/actions/core';
import * as interventionDetailsActions from 'modules/interventions/actions/details';
import * as actionTypes from 'modules/interventions/actionTypes';
import { OPEN_TSCANS_IMPORT_MODAL, FOLLOW_FILE, UNFOLLOW_FILE } from 'modules/interventions/actionTypes';
import { getById, getSelectedIntervention, getSelectedInterventionId } from 'modules/interventions/selectors';
import { getInterventionDealerId } from 'modules/interventions/selectors/details';
import { refreshInterventionLeads } from 'modules/leads/sagas/intervention.saga';
import { filterPayers } from 'modules/payers/actions';
import { requestFetchModels } from 'modules/questionModels/sagas';
import { getTireQuotes } from 'modules/tireQuote/actions';
import { OPEN_TIRE_QUOTES_IMPORT_MODAL } from 'modules/tireQuote/actionTypes';
import { fetchTireScans } from 'modules/tireScan/actions';
import { createNotification } from 'modules/toast-notifications/actions';
import { getUserGroups } from 'modules/userGroups/actions';

import { getCurrentLocation } from 'selectors/router';
import InterventionService from 'services/api/InterventionService';
import { getAdvices } from 'services/ChecklistUtils';
import { getHyperlink } from 'utils/i18nUtils';

import { redirectToDetailsPage } from './dashboard.saga';
import { INSPECTION_TYPE_ENUM } from '../constants/inspections';
import {
  getMissingInspectionRequests,
  castEntityToInspection,
  getMissingInspectionByInspectionType,
} from '../utils/inspectionUtils';

const INTERVENTION_PATHS = Object.values(INTERVENTION_PATHS_BY_TYPE).flat();

const simpleRequests = {
  [actionTypes.UPDATE_KIOSK_PREFERENCES]: { notifications: { success: () => undefined } },
  [FOLLOW_FILE]: {
    notifications: {
      success: {
        id: 'notifications.follow.success',
        defaultMessage:
          'You have successfully followed this file and will now receive notifications according to your <a>selected settings.</a>',
        values: { a: getHyperlink({ href: PATH.USER_NOTIFICATIONS_SETTINGS.path, target: '_blank' }) },
      },
    },
  },
  [UNFOLLOW_FILE]: {
    notifications: {
      success: {
        id: 'notifications.unfollow.success',
        defaultMessage:
          'You will no longer receive notifications for this file depending on your <a>selected settings.</a>',
        values: { a: getHyperlink({ href: PATH.USER_NOTIFICATIONS_SETTINGS.path, target: '_blank' }) },
      },
    },
  },
};

function* loadDealerInformation(dealerId) {
  yield put(selectDealer(dealerId, { projection: PROJECTION.CONFIG }));
  yield put(getUserGroups(dealerId));
}

const MODE_MAPPING = {
  reminders: TECHNICIAN,
  aftersales: TECHNICIAN,
  'parts-reminders': PARTS,
  bodyworks: TECHNICIAN,
};

function* initializePayers(intervention) {
  try {
    const { checklists } = intervention;

    const payerIdsToLoad = new Set();
    for (const { payerId } of getAdvices(checklists)) {
      if (payerId) {
        payerIdsToLoad.add(payerId);
      }
    }

    if (payerIdsToLoad.size) {
      yield putAndWait(
        filterPayers({ ids: [...payerIdsToLoad], projection: PROJECTION.BASIC, size: payerIdsToLoad.size })
      );
    }
    // eslint-disable-next-line no-empty
  } catch {}
}

/**
 * Initializes and loads all inspections for an intervention details page.
 *
 * This is the main orchestration saga that:
 * 1. Fetches recent inspections via the /inspections endpoint (time-scoped)
 * 2. Identifies which inspections are missing by checking checklist foreign keys
 * 3. Loads missing inspections in parallel via type-specific endpoints
 * 4. Combines all inspections (recent + missing) into a single list
 * 5. Dispatches success action to store the complete inspection list
 *
 * @param {import("../types/actions").FetchInspectionsAction} action
 */
function* onFetchInspectionsWorker({ payload }) {
  const { interventionId } = payload;
  const intervention = yield select(state => getById(state, interventionId));
  try {
    const { data: inspections } = yield call(restClient.request, payload.request);
    const missingInspections = getMissingInspectionByInspectionType(intervention, inspections);
    const requestActions = getMissingInspectionRequests(intervention, inspections);

    let loadedInspections = {};
    if (Object.entries(requestActions).length > 0) {
      loadedInspections = yield all(requestActions);
    }

    const formattedInspections = Object.values(INSPECTION_TYPE_ENUM).reduce((acc, type) => {
      const typeInspections = [
        ...(loadedInspections[type]?.payload?.content?.map(entity => castEntityToInspection(entity, type)) ?? []),
        ...(!loadedInspections[type] ? missingInspections[type] ?? [] : []),
      ];

      return [...acc, ...typeInspections];
    }, inspections);

    yield put(interventionDetailsActions.fetchInspectionsSuccess(interventionId, formattedInspections));
    // eslint-disable-next-line no-unused-vars
  } catch (error) {
    /* Nothing to do */
  }
}

export function* loadDetailsPageContent() {
  const location = yield select(getCurrentLocation);
  const {
    params: { interventionId, mode: modeParams },
  } = matchPath({ path: '/:mode/:interventionId' }, location);

  const meta = { readMessages: true, source: 'intervention-details' };
  const mode = MODE_MAPPING[modeParams] ?? modeParams;

  // 1. Critical path: fetch intervention data (blocks until complete)
  yield put(interventionCoreActions.fetchIntervention(interventionId, mode.toUpperCase(), meta));
  const { payload: intervention } = yield take(actionTypes.FETCH_INTERVENTION_SUCCESS);

  yield put(interventionDetailsActions.selectIntervention(interventionId));

  if (matchPath(PATH.SALES_DETAILED.path, location)) {
    yield fork(refreshInterventionLeads);
  }

  const dealerId = intervention.dealerId ?? intervention.dealer.id;

  // 2. Fire inspections immediately (no dealer dependency) + load dealer info in parallel
  yield all([
    call(loadDealerInformation, dealerId),
    put(interventionDetailsActions.fetchInspections(interventionId, mode)),
  ]);

  // 3. Defer non-critical calls: don't block first render
  // requestFetchModels internally waits for FETCH_DEALER_SUCCESS if needed
  yield fork(requestFetchModels, intervention.module, dealerId);
  yield fork(initializePayers, intervention);
  yield put(
    interventionDetailsActions.fetchLinkedInterventions(
      modeParams === 'parts-reminders' ? PARTS : mode.toUpperCase(),
      interventionId
    )
  );
}

function* afterUpdateIntervention({ payload: intervention }) {
  const { id: interventionId, mode } = intervention;
  yield put(interventionDetailsActions.fetchInspections(interventionId, mode));
}

function* afterImportInspections({ payload }) {
  const { intervention } = payload;
  const { id: interventionId, mode } = intervention;
  yield put(interventionDetailsActions.fetchInspections(interventionId, mode));
}

function* initializeDetailsPage() {
  const location = yield select(getCurrentLocation);
  const {
    params: { interventionId },
  } = matchPath({ path: '/:mode/:interventionId' }, location);

  const selectedInterventionId = yield select(getSelectedInterventionId);
  const isNotAlreadySelected = selectedInterventionId !== interventionId;

  yield put(interventionDetailsActions.interventionIsReady());
  if (isNotAlreadySelected) {
    yield call(loadDetailsPageContent);
  }
}

function* afterFetchIntervention({ payload: intervention, meta }) {
  const { readMessages = false } = meta;
  // Fetch messages
  const haveUnreadMessage = intervention?.conversationContext?.unreadCount > 0;
  if (readMessages && haveUnreadMessage) {
    yield put(interventionDetailsActions.readInterventionMessages(intervention.id, intervention.mode));
  }
}

function* onFetchInterventionLink({ payload: { id: interventionId, mode }, meta }) {
  const { error } = yield take([
    actionTypes.FETCH_INTERVENTION_LINK_SUCCESS,
    actionTypes.FETCH_INTERVENTION_LINK_FAILURE,
  ]);
  if (!error) {
    if (meta?.source === 'intervention-details') {
      yield put(interventionCoreActions.patchStatus(interventionId, mode, SENT));
    }
  } else {
    const notify = meta?.silent !== true;
    if (notify) {
      yield put(createNotification({ type: 'error', message: 'http.error.message' }));
    }
  }
}

function* fetchInterventionPaymentHistory({ payload: { id } }) {
  try {
    const response = yield call(InterventionService.getPaymentHistory, id);
    yield put(interventionDetailsActions.fetchInterventionPaymentHistorySuccess(response.data));
  } catch (error) {
    yield put(interventionDetailsActions.fetchInterventionPaymentHistoryFailure(error));
  }
}

function* openScanImportModal(actionCreator) {
  const { vehicleInfo = {} } = yield select(getSelectedIntervention);
  const dealerId = yield select(getInterventionDealerId);
  const { registration } = vehicleInfo ?? {};
  if (registration?.length >= REGISTRATION_MIN_SIZE) {
    yield put(
      actionCreator(
        {
          dealerId,
          registration,
        },
        { source: 'modal' }
      )
    );
  }
}

function* openTScansImportModal() {
  yield fork(openScanImportModal, fetchTireScans);
}

function* openTireQuotesImportModal() {
  yield fork(openScanImportModal, getTireQuotes);
}

function* openBodyScansImportModal() {
  yield fork(openScanImportModal, getBodyScans);
}

function* onLeaveDetailsPage() {
  yield put(interventionDetailsActions.closeApproveManuallyModal());
}

function* onDetailsPageWorker() {
  yield takeEvery(actionTypes.FETCH_INTERVENTION_SUCCESS, afterFetchIntervention);
  yield takeEvery(actionTypes.CREATE_INTERVENTION_SUCCESS, redirectToDetailsPage);
  yield takeEvery(actionTypes.POST_DUPLICATE_INTERVENTION_SUCCESS, redirectToDetailsPage);

  yield takeEvery(OPEN_TSCANS_IMPORT_MODAL, openTScansImportModal);
  yield takeEvery(OPEN_TIRE_QUOTES_IMPORT_MODAL, openTireQuotesImportModal);
  yield takeEvery(OPEN_BODY_SCANS_IMPORT_MODAL, openBodyScansImportModal);
  yield takeEvery(actionTypes.UPDATE_INTERVENTION_SUCCESS, afterUpdateIntervention);
  yield takeEvery(actionTypes.IMPORT_AUDITS_OR_TSCANS_SUCCESS, afterImportInspections);

  yield call(initializeDetailsPage);
}

export default function* interventionDetailsSagas() {
  // Details pages
  yield scopeToPage(INTERVENTION_PATHS, onDetailsPageWorker, onLeaveDetailsPage);
  yield takeEvery(actionTypes.FETCH_INTERVENTION_LINK_REQUEST, onFetchInterventionLink);
  yield takeLatest(actionTypes.FETCH_INSPECTIONS, onFetchInspectionsWorker);
  yield takeLatest(actionTypes.FETCH_INTERVENTION_PAYMENT_HISTORY_REQUEST, fetchInterventionPaymentHistory);

  yield all(simpleRequestsLoader(simpleRequests));
}
